import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';

/**
 * Accent-fold preserving length so match offsets align 1:1 with the original
 * (we slice the ORIGINAL to keep accents on screen). Per-character: fold a char
 * only when folding doesn't change its length (a precomposed `é` → `e`, both
 * length 1); otherwise keep it. Mirrors BrandAxisPage.foldAccentsPreserveLen.
 */
export function foldPreserveLen(s: string): string {
  let out = '';
  for (const ch of s) {
    const f = ch.normalize('NFD').replace(/[̀-ͯ]/g, '');
    out += f.length === ch.length ? f : ch;
  }
  return out;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export type HighlightClass = 'product' | 'sku' | 'source' | 'retailer' | 'brand';

const STYLES: Record<HighlightClass, object> = {
  product: { bgcolor: 'rgba(0,221,179,0.25)', color: '#00A88A', fontWeight: 700 },
  sku: { bgcolor: 'rgba(37,99,235,0.15)', color: '#2563EB', fontWeight: 700 },
  source: {
    bgcolor: 'rgba(124,58,237,0.12)',
    color: '#7C3AED',
    fontWeight: 600,
    textDecoration: 'underline dotted',
  },
  retailer: { bgcolor: 'rgba(46,125,50,0.18)', color: '#2E7D32', fontWeight: 700 },
  brand: { bgcolor: 'rgba(237,108,2,0.15)', color: '#ED6C02', fontWeight: 700 },
};

export interface HighlightTerm {
  term: string;
  cls: HighlightClass;
}

/**
 * Highlight product names / SKUs / source domains / the retailer domain in an AI
 * answer. Matches against an accent-folded copy (so "Prévifrance" matches
 * "previfrance") but renders slices of the ORIGINAL so accents stay on screen —
 * same technique as BrandAxisPage. Longer terms first so a full name wins over a
 * bare token.
 */
export function highlightText(text: string, terms: HighlightTerm[]): React.ReactNode[] {
  if (!text) return [text];
  const valid = terms.filter((t) => t.term && t.term.trim().length >= 3);
  if (!valid.length) return [text];
  const byTerm = new Map<string, HighlightClass>();
  for (const t of valid) {
    const k = foldPreserveLen(t.term).toLowerCase();
    if (!byTerm.has(k)) byTerm.set(k, t.cls); // first class wins per term
  }
  const ordered = [...byTerm.keys()].sort((a, b) => b.length - a.length);
  const folded = foldPreserveLen(text).toLowerCase();
  const regex = new RegExp(`(${ordered.map(escapeRegex).join('|')})`, 'gi');

  const out: React.ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of folded.matchAll(regex)) {
    const start = m.index ?? 0;
    const end = start + m[0].length;
    if (start > last) out.push(<span key={`t${i++}`}>{text.slice(last, start)}</span>);
    const cls = byTerm.get(m[0].toLowerCase()) || 'source';
    out.push(
      <Box key={`h${i++}`} component="span" sx={{ px: 0.4, borderRadius: 0.5, ...STYLES[cls] }}>
        {text.slice(start, end)}
      </Box>,
    );
    last = end;
  }
  if (last < text.length) out.push(<span key={`t${i++}`}>{text.slice(last)}</span>);
  return out;
}

// Inline markdown (**bold**, *italic*, `code`, [text](url)) with highlighting
// applied to the plain-text spans. Adapted from BrandAxisPage.renderInlineMarkdown.
function renderInline(text: string, terms: HighlightTerm[], key: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re =
    /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|(?<![*\w])\*([^*\n]+)\*(?!\*)|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let idx = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(...highlightText(text.slice(last, m.index), terms));
    if (m[1] && m[2]) {
      out.push(
        <a key={`${key}a${idx}`} href={m[2]} target="_blank" rel="noopener noreferrer" style={{ color: '#7C3AED' }}>
          {highlightText(m[1], terms)}
        </a>,
      );
    } else if (m[3] || m[4]) {
      out.push(<strong key={`${key}b${idx}`}>{highlightText(m[3] || m[4] || '', terms)}</strong>);
    } else if (m[5]) {
      out.push(<em key={`${key}i${idx}`}>{highlightText(m[5], terms)}</em>);
    } else if (m[6]) {
      out.push(
        <Box key={`${key}c${idx}`} component="code" sx={{ fontFamily: 'monospace', fontSize: '0.85em', bgcolor: 'rgba(0,0,0,0.06)', px: 0.5, borderRadius: 0.5 }}>
          {m[6]}
        </Box>,
      );
    }
    last = m.index + m[0].length;
    idx++;
  }
  if (last < text.length) out.push(...highlightText(text.slice(last), terms));
  return out;
}

/** Split a markdown table row `| a | b |` into trimmed cells (drops the outer pipes). */
function splitTableRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  // Split on unescaped pipes.
  return s.split(/(?<!\\)\|/).map((c) => c.replace(/\\\|/g, '|').trim());
}
/** A GFM table separator row: `|---|:--:|` etc. (only -, :, |, spaces). */
function isTableSeparator(line: string): boolean {
  const t = line.trim();
  return /\|/.test(t) && /^[\s|:-]+$/.test(t) && /-/.test(t);
}
/** A candidate table row: contains a pipe and isn't a heading/hr. */
function looksLikeTableRow(line: string): boolean {
  return line.includes('|') && !/^#{1,6}\s/.test(line.trim());
}

/**
 * Render an AI answer's markdown to React with product/SKU/source highlighting.
 * Handles the block markdown engines actually emit: `#`/`##` headers, `-`/`*`/`•`
 * and `1.` list items, GFM tables (`| a | b |` + `|---|`), and paragraphs. Inline
 * bold/italic/links/code within each cell/line.
 */
export function renderMarkdown(text: string, terms: HighlightTerm[]): React.ReactNode {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  let k = 0;

  const flushPara = () => {
    if (!para.length) return;
    blocks.push(
      <Box key={`p${k++}`} sx={{ mb: 1 }}>
        {renderInline(para.join(' '), terms, `p${k}`)}
      </Box>,
    );
    para = [];
  };

  for (let idx = 0; idx < lines.length; idx++) {
    const raw = lines[idx];
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      continue;
    }

    // GFM table: a header row immediately followed by a separator row.
    if (
      looksLikeTableRow(line) &&
      idx + 1 < lines.length &&
      isTableSeparator(lines[idx + 1])
    ) {
      flushPara();
      const header = splitTableRow(line);
      const rows: string[][] = [];
      let j = idx + 2;
      for (; j < lines.length; j++) {
        const r = lines[j];
        if (!r.trim() || !looksLikeTableRow(r) || isTableSeparator(r)) break;
        rows.push(splitTableRow(r));
      }
      blocks.push(
        <Box key={`tbl${k++}`} sx={{ overflowX: 'auto', mb: 1.5 }}>
          <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: '0.8rem' } }}>
            <TableHead>
              <TableRow>
                {header.map((c, ci) => (
                  <TableCell key={ci} sx={{ fontWeight: 700, bgcolor: 'action.hover' }}>
                    {renderInline(c, terms, `th${k}-${ci}`)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, ri) => (
                <TableRow key={ri}>
                  {header.map((_, ci) => (
                    <TableCell key={ci}>{renderInline(r[ci] ?? '', terms, `td${k}-${ri}-${ci}`)}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>,
      );
      idx = j - 1;
      continue;
    }

    const h = line.match(/^(#{1,4})\s+(.*)$/);
    const li = line.match(/^\s*(?:[-*•]|\d+[.)])\s+(.*)$/);
    if (h) {
      flushPara();
      blocks.push(
        <Box key={`h${k++}`} sx={{ fontWeight: 700, fontSize: h[1].length <= 2 ? '1.05rem' : '0.95rem', mt: 1, mb: 0.5 }}>
          {renderInline(h[2], terms, `h${k}`)}
        </Box>,
      );
    } else if (li) {
      flushPara();
      blocks.push(
        <Box key={`li${k++}`} sx={{ display: 'flex', gap: 1, mb: 0.5, pl: 1 }}>
          <Box component="span" sx={{ color: 'text.secondary' }}>•</Box>
          <Box component="span">{renderInline(li[1], terms, `li${k}`)}</Box>
        </Box>,
      );
    } else {
      para.push(line);
    }
  }
  flushPara();
  return <>{blocks}</>;
}
